import { useState } from "react";
import { CloudMast } from "./CloudMast";
import { resolveAdClick, type AdClick } from "../lib/adClick";

// Shown in place of the wizard when Aurora is opened on a phone, a tablet,
// or a desktop window too narrow to run the flow (Shell decides; see
// isMobileDevice + the viewport check there). The wizard needs a computer
// for two reasons the user can't work around on mobile: they upload a PDF
// message export, and the deliverable is an encrypted file they save and
// re-open offline — both poor-to-broken without a real file system.
//
// Hard block, no "continue anyway": letting someone finish a multi-minute
// flow only to discover the deliverable is unusable is the worse UX. The
// invite code is NOT consumed until an analysis launches, so a blocked user
// loses nothing — say so plainly; it's the thing they'd otherwise worry
// about. The email/copy-link affordances get the URL onto a real computer.
// Reuses the landing chrome.

export const PILOT_URL = "https://cloud.plaintosee.cc";

// The link the gate hands to the visitor's computer, with the ad click
// carried along. 92% of ad clicks arrive on phones (Google Ads, Jul–Oct
// 2026), every one of them lands here, and the bare PILOT_URL used to be
// all we sent across: the click ID stayed in the phone's cookie, so the
// desktop visit that actually minted showed up as organic. Putting the
// click ID in the URL lets adClick.ts's adClickFromSearch pick it up on the
// other device (a URL param beats the cookie by design). Still first-party
// only: the ID is Google's opaque click token, already in the visitor's
// own cookie, sent to the visitor's own address; it names the click, not
// the person. landed_at is deliberately not carried — the desktop landing
// is its own landing.
export function pilotUrlWithAdClick(
  click: AdClick | null,
  base: string = PILOT_URL,
): string {
  if (!click) return base;
  const params = new URLSearchParams();
  params.set(click.kind, click.id);
  if (click.src) params.set("src", click.src);
  return `${base}/?${params.toString()}`;
}

// Build the mailto: href for "Email this link to myself". No recipient — the
// mobile gate has no address on hand, so this opens a compose with the link
// in the body for the user to address to themselves. Extracted (and exported)
// so the URL construction is unit-testable without triggering navigation.
export function buildSelfEmailHref(url: string = PILOT_URL): string {
  const subject = encodeURIComponent("Aurora — open on a laptop");
  const body = encodeURIComponent(
    `Open this on a laptop or desktop computer to use Aurora:\n\n${url}\n`,
  );
  return `mailto:?subject=${subject}&body=${body}`;
}

export function DesktopRequired() {
  const [copied, setCopied] = useState(false);
  // Read once per render: the cookie was written by the marketing site on
  // the way in (or the click ID is on this page's own URL). The on-screen
  // URL stays bare for someone typing it by hand; email + copy carry the
  // attributed link.
  const link = pilotUrlWithAdClick(resolveAdClick());

  function emailSelf() {
    window.location.href = buildSelfEmailHref(link);
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be denied on iOS Safari — the URL is on screen to
      // long-press instead.
    }
  }

  return (
    <main className="landing">
     <div className="landing-wrap">
      <CloudMast />

      <section className="desktop-req">
        <div className="landing-eyebrow">One thing first</div>
        <h1 className="landing-title">Open Aurora on a computer.</h1>
        <p className="landing-lede">
          Aurora runs on a desktop or laptop browser. You'll upload your
          message export, and what you get back is an encrypted file you save
          and open on your own computer — both work best with a real keyboard
          and file system, which phones and tablets don't give you.
        </p>

        <div className="desktop-req-link">
          <span className="desktop-req-link-label">Open this on a computer</span>
          <div className="desktop-req-url">{PILOT_URL}</div>
          <div className="desktop-req-actions">
            <button type="button" className="apply-submit" onClick={emailSelf}>
              Email this link to myself
            </button>
            <button type="button" className="desktop-req-copy" onClick={copyLink}>
              {copied ? "Link copied ✓" : "Copy link"}
            </button>
          </div>
        </div>

        <div className="desktop-req-note">
          <p className="desktop-req-note-eyebrow">Your invitation is safe</p>
          <p>
            Nothing has been used yet — your access code is only spent when an
            analysis actually runs. Open the link above on a computer, and
            you'll pick up right here.
          </p>
        </div>
      </section>

      <footer className="landing-foot">
        <div className="landing-foot-left">Aurora · Built independently.</div>
        <div className="landing-foot-right">&copy; 2026 Aurora</div>
      </footer>
     </div>
    </main>
  );
}
